import type {TagColor} from "~/components/tag/tag.styles";
import type {SegmentationAxes, SegmentationType} from "~/pages/supplier-360/types/Supplier360Types";
import {COLORS} from "~/style/colors-ts";

/**
 * Mini-matrix order — reading order of a 2×2 grid whose x axis is spend value and whose y axis is
 * supply risk, so the high-risk row comes first: bottleneck | strategic / routine | leverage.
 */
export const SEGMENT_MATRIX_ORDER: SegmentationType[] = ['bottleneck', 'strategic', 'routine', 'leverage'];

export const SEGMENT_LABEL: Record<SegmentationType, string> = {
    strategic: 'Strategic',
    leverage: 'Leverage',
    bottleneck: 'Bottleneck',
    routine: 'Routine',
};

export const SEGMENT_DESCRIPTION: Record<SegmentationType, string> = {
    strategic: 'High value, high risk — partner and protect',
    leverage: 'High value, low risk — compete and consolidate',
    bottleneck: 'Low value, high risk — secure supply',
    routine: 'Low value, low risk — automate and simplify',
};

export const SEGMENT_COLOR: Record<SegmentationType, {main: string; tint: string}> = {
    strategic: {main: COLORS.success.main, tint: COLORS.success[100]},
    leverage: {main: COLORS.info.main, tint: COLORS.primary[100]},
    bottleneck: {main: COLORS.error.main, tint: COLORS.error[100]},
    routine: {main: COLORS.grey[700], tint: COLORS.grey[50]},
};

export const SEGMENT_TAG_COLOR: Record<SegmentationType, TagColor> = {
    strategic: 'success',
    leverage: 'info',
    bottleneck: 'error',
    routine: 'default',
};

/**
 * Composite risk index, 0–100 — the design's `riskIndex()` ported verbatim: supply risk counts
 * double, and the denominator is 30 rather than the 25 the four axes can actually add up to, so a
 * maximum-risk supplier reads 83/100 rather than 100. Kept as the design has it, so the number
 * shown here matches the number shown in the prototype.
 */
export function computeRiskIndex(axes: SegmentationAxes): number {
    const total = axes.supply_risk * 2 + axes.switching_difficulty + axes.substitutes + axes.business_criticality;
    return Math.round((total / 30) * 100);
}

/**
 * The quadrant a set of ratings lands in — the design's `segmentOf()` and the backend's
 * `processor_quadrant.derive_segment()`, which is still what actually gets stored: `segment` is
 * derived server-side from spend value × supply risk and the picked value is ignored (docs §2.12).
 * Used to tell the scorer what the axes imply, so a saved score can't surprise them.
 */
export function deriveSegment(axes: SegmentationAxes): SegmentationType {
    if (axes.spend_value >= 3) {
        return axes.supply_risk >= 3 ? 'strategic' : 'leverage';
    }
    return axes.supply_risk >= 3 ? 'bottleneck' : 'routine';
}

/** `05 Aug 2026`, the date format the design's segmentation block uses throughout. */
export function formatSegmentationDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
}
